import { acceptHMRUpdate, defineStore } from 'pinia'

import { useInfluencersStore } from './influencers'
import { useMiscSendtrackFiltersStore } from './miscSendtrackFilters'
import { useTagStore } from './tag'
import { useUserBandStore } from './userBand'

import { useProvideCoreFetch } from '~/composables/useProvideCoreFetch'

import {
  getCurrentUserSearches,
  getSearchByIdResults,
  getSearchResultsFromText,
  getSearchSuggestions,
  getUserRecommendations,
} from '~/helpers/elasticSearch/api'

import type Tag from '~/entities/tag'
import type TagType from '~/entities/tagType'
import type {
  ApiSearchByConfig,
  ApiSuggestResult,
  AvailableSearchResultTypes,
  SearchApiTemplate,
  SearchResultInfluencer,
  SearchResultTag,
} from '~/types/elasticsearchApi'
import type { StatsV3Influencer } from '~/types/influencer'

const createApiSuggestResult = () => ({
  influencers: {
    hits: [],
    total: 0,
  },
  tags: {
    hits: [],
    total: 0,
  },
})

const initialState = () => ({
  text: '',
  previousText: '',
  lastSearchText: '',
  loading: false,
  currentSearchContext: null as SearchResultTag | null,
  userRecommendations: createApiSuggestResult() as ApiSuggestResult,
  previousSearches: createApiSuggestResult() as ApiSuggestResult,
  currentSuggestions: createApiSuggestResult() as ApiSuggestResult,
  results: [] as StatsV3Influencer['id'][],
  searchExecBackup: null as ApiSearchByConfig | null,
})

const state = initialState

export type MiscSendtrackSearchState = ReturnType<typeof state>

export const useMiscSendtrackSearchStore = defineStore(
  'miscSendtrackSearch',
  () => {
    const { coreFetch } = useProvideCoreFetch()
    const { $pinia } = useNuxtApp()
    const influencersStore = useInfluencersStore($pinia)
    const tagStore = useTagStore($pinia)
    const miscSendtrackFiltersStore = useMiscSendtrackFiltersStore($pinia)
    const userBandStore = useUserBandStore($pinia)

    const text = ref('')
    const previousText = ref('')
    const lastSearchText = ref('')
    const loading = ref(false)
    const currentSearchContext = ref<SearchResultTag | null>(null)
    const userRecommendations = ref<ApiSuggestResult>(createApiSuggestResult())
    const previousSearches = ref<ApiSuggestResult>(createApiSuggestResult())
    const currentSuggestions = ref<ApiSuggestResult>(createApiSuggestResult())
    const results = ref<StatsV3Influencer['id'][]>([])
    const searchExecBackup = ref<ApiSearchByConfig | null>(null)

    function SET_LOADING(data: boolean): void {
      loading.value = data
    }

    function SET_SEARCH_EXEC_CONFIG(newConfig: ApiSearchByConfig | null) {
      searchExecBackup.value = newConfig
    }

    function SET_TEXT(newText: string): void {
      text.value = newText
    }

    function SET_PREVIOUS_TEXT(newText: string): void {
      previousText.value = newText
    }

    function SET_LAST_SEARCH_TEXT(newText: string): void {
      lastSearchText.value = newText
    }

    function SET_RESULTS(hits: StatsV3Influencer['id'][]) {
      results.value = hits
    }

    function SET_CURRENT_SUGGESTION_BY_KEY({
      value,
      key,
    }: {
      value: SearchApiTemplate<AvailableSearchResultTypes>
      key: keyof ApiSuggestResult
    }): void {
      if (currentSuggestions.value[key])
        // @ts-expect-error frfr
        currentSuggestions.value[key] = value
      else console.warn(`[SEARCH STORE] - "${key}" is not a valid key name`)
    }

    function SET_USER_RECOMMENDATIONS(
      newUserRecommendations: ApiSuggestResult,
    ) {
      userRecommendations.value = newUserRecommendations
    }

    function SET_PREVIOUS_SEARCH(newPreviousSearches: ApiSuggestResult): void {
      previousSearches.value = newPreviousSearches
    }

    async function GET_AND_SET_USER_RECOMMENDATIONS() {
      // Ignore if no band is logged in
      if (!userBandStore.id) return

      try {
        const resp = await getUserRecommendations(coreFetch, userBandStore.id)
        SET_USER_RECOMMENDATIONS(resp)
      } catch (err) {
        // mute error
      }
    }

    async function GET_AND_SET_USER_HISTORY() {
      try {
        SET_LOADING(true)

        const { hits } = await getCurrentUserSearches(coreFetch, {
          limit: 5,
          offset: 0,
        })
        const influencerHits = hits
          .filter((e) => e.type === 'influencer')
          .reduce((accumulator, elem) => {
            const influencer: StatsV3Influencer | undefined =
              influencersStore.GET_BY_ID(elem.value)
            if (influencer) {
              accumulator.push({
                id: influencer.id,
                entity: influencer.entity,
                is_scout: influencer.is_scout,
                visible: influencer.visible,
                slug: influencer.slug,
              })
            }

            return accumulator
          }, [] as SearchResultInfluencer[])

        const tagHits = hits
          .filter((e) => e.type === 'tag')
          .reduce((accumulator, elem) => {
            const tag: Tag | undefined = tagStore.GET_TAG_FROM_ID(elem.value)
            const tagType: TagType | undefined = tagStore.GET_TYPE_FROM_ID(
              tag?.type ?? 0,
            )

            if (tag && tagType) {
              accumulator.push({
                name: tag.name,
                type_id: tagType.id,
                type_name: tagType.name,
                id: tag.id,
                count: 0,
              })
            }

            return accumulator
          }, [] as SearchResultTag[])

        SET_PREVIOUS_SEARCH({
          influencers: {
            hits: influencerHits,
            total: influencerHits.length,
          },
          tags: {
            hits: tagHits,
            total: tagHits.length,
          },
        } as unknown as ApiSuggestResult)
      } catch (_) {
        // mute error
      }
      SET_LOADING(false)
    }

    async function EXEC_BY_TEXT(newText?: string) {
      newText = newText ?? text.value
      try {
        SET_RESULTS(
          (
            await getSearchResultsFromText(coreFetch, {
              search: newText,
              filters: {
                // TODO: fix this type
                // @ts-expect-error frfr
                tags: miscSendtrackFiltersStore.SEND_DATA,
              },
              limit: Number.MAX_SAFE_INTEGER,
            })
          ).hits.map((e) => e.id),
        )
        SET_LAST_SEARCH_TEXT(newText)
        SET_SEARCH_EXEC_CONFIG(null)
      } catch (_) {
        // mute error
      }
    }

    async function EXEC_BY_ID({ by, id }: ApiSearchByConfig): Promise<void> {
      try {
        const { hits } = await getSearchByIdResults(coreFetch, {
          by,
          // TODO: fix this type
          // @ts-expect-error frfr
          filters: { tags: miscSendtrackFiltersStore.SEND_DATA },
          id,
          limit: Number.MAX_SAFE_INTEGER,
          offset: 0,
        })

        if (by === 'tag') {
          SET_RESULTS(hits.map((e) => e.id))
          SET_LAST_SEARCH_TEXT(text.value)
          SET_SEARCH_EXEC_CONFIG({ by, id })
        } else {
          SET_RESULTS([])
          SET_SEARCH_EXEC_CONFIG(null)
        }
      } catch (_) {
        // mute error
      }
    }

    async function GET_AND_SET_SUGGESTIONS_FROM_NEW_SEARCH_TEXT(
      newSearchText: string,
    ): Promise<void> {
      if (newSearchText !== text.value) SET_TEXT(newSearchText)

      SET_LOADING(true)

      try {
        const results = await getSearchSuggestions(coreFetch, {
          search: text.value,
          // TODO: fix this type
          // @ts-expect-error frfr
          filters: { tags: miscSendtrackFiltersStore.SEND_DATA },
        })

        for (const key in results) {
          const typedKey = key as keyof ApiSuggestResult
          SET_CURRENT_SUGGESTION_BY_KEY({
            key: typedKey,
            value: results[typedKey],
          })
        }
      } catch (_) {
        // mute error
      }
      SET_LOADING(false)
    }

    async function RESET(
      { shouldExecuteFilters }: { shouldExecuteFilters: boolean } = {
        shouldExecuteFilters: true,
      },
    ) {
      if (
        shouldExecuteFilters &&
        miscSendtrackFiltersStore.NB_FILTER_SELECTED > 0
      )
        await miscSendtrackFiltersStore.EXECUTE()

      Object.keys(initialState()).forEach((key) => {
        const ref = getStateRef(key as keyof MiscSendtrackSearchState)
        if (ref)
          ref.value = initialState()[key as keyof MiscSendtrackSearchState]
      })
    }

    function getStateRef(key: keyof MiscSendtrackSearchState) {
      switch (key) {
        case 'text':
          return text
        case 'previousText':
          return previousText
        case 'lastSearchText':
          return lastSearchText
        case 'loading':
          return loading
        case 'currentSearchContext':
          return currentSearchContext
        case 'userRecommendations':
          return userRecommendations
        case 'previousSearches':
          return previousSearches
        case 'currentSuggestions':
          return currentSuggestions
        case 'results':
          return results
        case 'searchExecBackup':
          return searchExecBackup
        default:
          return null
      }
    }

    return {
      // state
      text,
      previousText,
      lastSearchText,
      loading,
      currentSearchContext,
      userRecommendations,
      previousSearches,
      currentSuggestions,
      results,
      searchExecBackup,

      // actions
      SET_LOADING,
      SET_SEARCH_EXEC_CONFIG,
      SET_TEXT,
      SET_PREVIOUS_TEXT,
      SET_LAST_SEARCH_TEXT,
      SET_RESULTS,
      SET_CURRENT_SUGGESTION_BY_KEY,
      SET_USER_RECOMMENDATIONS,
      SET_PREVIOUS_SEARCH,
      GET_AND_SET_USER_RECOMMENDATIONS,
      GET_AND_SET_USER_HISTORY,
      EXEC_BY_TEXT,
      EXEC_BY_ID,
      GET_AND_SET_SUGGESTIONS_FROM_NEW_SEARCH_TEXT,
      RESET,
    }
  },
)

if (import.meta.hot) {
  import.meta.hot.accept(
    acceptHMRUpdate(useMiscSendtrackSearchStore, import.meta.hot),
  )
}
